/**
 * Rascunho do editor de manuais (Área do PCP): o que a pessoa está editando
 * antes de "Salvar versão". Funções puras, sem React — a tela só despacha
 * ações e o teste cobre cada uma.
 *
 * O rascunho tem o MESMO formato que o backend aceita no PUT (e que a versão
 * guarda): campos do cabeçalho, identificador, `secoes` e `passos` na ordem.
 * Passo novo nasce com chave gerada aqui; o backend aceita chave desconhecida
 * como passo novo com aquela identidade (ADR-0087).
 */

export const CAMPOS_DO_CABECALHO = Object.freeze([
  "objetivo",
  "quando",
  "envolvidos",
  "sistemas",
  "regras",
  "entrega_final",
  "erros_comuns",
  "em_aberto",
]);
export const CAMPOS_DO_IDENTIFICADOR = Object.freeze([
  "exige_identificador",
  "rotulo_identificador",
  "formato_identificador",
  "exemplo_identificador",
]);
export const CAMPOS_DO_PASSO = Object.freeze(["titulo_curto", "sistema", "texto", "aviso", "trecho_copiavel"]);

export const ROTULO_DO_CAMPO = Object.freeze({
  titulo: "Título",
  objetivo: "Objetivo",
  quando: "Quando",
  envolvidos: "Envolvidos",
  sistemas: "Sistemas",
  regras: "Regras",
  entrega_final: "Entrega final",
  erros_comuns: "Erros comuns",
  em_aberto: "Em aberto",
  titulo_curto: "Título curto",
  sistema: "Sistema",
  texto: "Texto do passo",
  aviso: "Aviso",
  trecho_copiavel: "Trecho para copiar",
});

export function novaChave() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  // Fallback para ambiente sem randomUUID (teste antigo): UUID v4 suficiente.
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = Math.floor(Math.random() * 16);
    return (c === "x" ? r : (r % 4) + 8).toString(16);
  });
}

export function passoVazio(secao = null) {
  return {
    chave: novaChave(),
    secao,
    titulo_curto: "",
    sistema: "",
    texto: "",
    aviso: "",
    trecho_copiavel: "",
    referencia: null,
  };
}

export function rascunhoVazio() {
  return {
    titulo: "",
    ...Object.fromEntries(CAMPOS_DO_CABECALHO.map((c) => [c, ""])),
    exige_identificador: false,
    rotulo_identificador: "Identificação",
    formato_identificador: "",
    exemplo_identificador: "",
    secoes: [],
    passos: [passoVazio()],
  };
}

/** Rascunho a partir do detalhe da API (tira o que é só de exibição). */
export function rascunhoDaTarefa(tarefa) {
  return {
    titulo: tarefa.titulo || "",
    ...Object.fromEntries(CAMPOS_DO_CABECALHO.map((c) => [c, tarefa[c] || ""])),
    ...Object.fromEntries(CAMPOS_DO_IDENTIFICADOR.map((c) => [c, tarefa[c] ?? (c === "exige_identificador" ? false : "")])),
    secoes: (tarefa.secoes || []).map((s) => ({ chave: s.chave, titulo: s.titulo })),
    passos: (tarefa.passos || []).map((p) => ({
      chave: p.chave,
      secao: p.secao || null,
      ...Object.fromEntries(CAMPOS_DO_PASSO.map((c) => [c, p[c] || ""])),
      referencia: p.referencia || null,
    })),
  };
}

/** Corpo do POST/PUT (sem a `versao_base`, que o service acrescenta). */
export function paraEnvio(rascunho) {
  return {
    ...rascunho,
    secoes: rascunho.secoes.map((s) => ({ chave: s.chave, titulo: s.titulo })),
    passos: rascunho.passos.map((p) => ({ ...p })),
  };
}

function mover(lista, de, para) {
  if (para < 0 || para >= lista.length || de === para) return lista;
  const copia = [...lista];
  const [item] = copia.splice(de, 1);
  copia.splice(para, 0, item);
  return copia;
}

function trocarPasso(rascunho, indice, mudanca) {
  return {
    ...rascunho,
    passos: rascunho.passos.map((p, i) => (i === indice ? { ...p, ...mudanca } : p)),
  };
}

/** Reducer das edições. Ação desconhecida devolve o rascunho intacto. */
export function editarRascunho(rascunho, acao) {
  switch (acao.tipo) {
    case "campo":
      return { ...rascunho, [acao.campo]: acao.valor };
    case "editarPasso":
      return trocarPasso(rascunho, acao.indice, { [acao.campo]: acao.valor });
    case "adicionarPasso": {
      const vizinho = rascunho.passos[acao.indice];
      const posicao = acao.onde === "acima" ? acao.indice : acao.indice + 1;
      const passos = [...rascunho.passos];
      passos.splice(Math.max(0, posicao), 0, passoVazio(vizinho?.secao ?? null));
      return { ...rascunho, passos };
    }
    case "duplicarPasso": {
      const original = rascunho.passos[acao.indice];
      if (!original) return rascunho;
      const passos = [...rascunho.passos];
      passos.splice(acao.indice + 1, 0, { ...original, chave: novaChave() });
      return { ...rascunho, passos };
    }
    case "removerPasso":
      return { ...rascunho, passos: rascunho.passos.filter((_, i) => i !== acao.indice) };
    case "moverPasso":
      return { ...rascunho, passos: mover(rascunho.passos, acao.de, acao.para) };
    case "adicionarSecao":
      return { ...rascunho, secoes: [...rascunho.secoes, { chave: novaChave(), titulo: acao.titulo || "Nova seção" }] };
    case "renomearSecao":
      return {
        ...rascunho,
        secoes: rascunho.secoes.map((s) => (s.chave === acao.chave ? { ...s, titulo: acao.titulo } : s)),
      };
    case "removerSecao":
      // Os passos da seção ficam sem seção: apagar o agrupador não apaga passo.
      return {
        ...rascunho,
        secoes: rascunho.secoes.filter((s) => s.chave !== acao.chave),
        passos: rascunho.passos.map((p) => (p.secao === acao.chave ? { ...p, secao: null } : p)),
      };
    case "moverSecao":
      return { ...rascunho, secoes: mover(rascunho.secoes, acao.de, acao.para) };
    case "restaurar":
      return acao.rascunho;
    default:
      return rascunho;
  }
}

function igual(a, b) {
  return JSON.stringify(a) === JSON.stringify(b);
}

/** Quantas alterações o rascunho tem em relação ao carregado (o "2 não salvas"). */
export function contarAlteracoes(original, atual) {
  if (!original) return 0;
  let total = 0;
  ["titulo", ...CAMPOS_DO_CABECALHO, ...CAMPOS_DO_IDENTIFICADOR].forEach((campo) => {
    if (!igual(original[campo], atual[campo])) total += 1;
  });
  if (!igual(original.secoes, atual.secoes)) total += 1;
  const antes = new Map(original.passos.map((p) => [p.chave, p]));
  const depois = new Map(atual.passos.map((p) => [p.chave, p]));
  depois.forEach((passo, chave) => {
    if (!antes.has(chave) || !igual(antes.get(chave), passo)) total += 1;
  });
  antes.forEach((_, chave) => {
    if (!depois.has(chave)) total += 1;
  });
  const comuns = (lista, outro) => lista.filter((p) => outro.has(p.chave)).map((p) => p.chave);
  if (!igual(comuns(original.passos, depois), comuns(atual.passos, antes))) total += 1;
  return total;
}

const NUMERO_A_MAO = /\b(passos?|tarefa)\s+\d+/i;

/**
 * Passos cujo texto ou aviso cita "passo N" / "Tarefa N" escrito à mão: esses
 * números não acompanham a reordenação (ex.: "usado de novo no passo 9").
 */
export function passosComNumeroAMao(rascunho) {
  return rascunho.passos
    .map((p, indice) => ({ indice, chave: p.chave, achado: `${p.texto} ${p.aviso}`.match(NUMERO_A_MAO)?.[0] }))
    .filter((p) => p.achado);
}

/** Problemas que impedem salvar, com a frase pronta (o backend confere de novo). */
export function problemasDoRascunho(rascunho) {
  const problemas = [];
  if (!rascunho.titulo.trim()) problemas.push("Dê um título à tarefa.");
  rascunho.passos.forEach((p, i) => {
    if (!p.texto.trim() && !p.referencia) problemas.push(`Passo ${i + 1}: escreva o que fazer neste passo.`);
    const ref = p.referencia;
    if (ref && !(ref.tarefa_id && ref.inicio && ref.fim)) {
      problemas.push(`Passo ${i + 1}: escolha a tarefa e os passos da referência (ou tire a referência).`);
    }
  });
  rascunho.secoes.forEach((s, i) => {
    if (!s.titulo.trim()) problemas.push(`Seção ${i + 1}: dê um título.`);
  });
  return problemas;
}
